/**
 * src/components/organizations/OrganizationsSection.tsx
 * Organization management — the tier between Country and Spatial Object.
 *
 * Two hosts, one component:
 *   - CountryManagementOverlay renders it scoped to one country, with
 *     writes enabled for that country's admin (registration is
 *     country-scoped on chain, so this is where it belongs).
 *   - AdminPanel renders it with `readOnly`, unscoped, for the protocol
 *     admin's overview.
 *
 * Reads come from the chain via /api/v2/organizations. Writes build
 * unsigned XDR there, get signed by the operator's own wallet, and go
 * back to /organizations/submit — the backend never signs, because the
 * contract's country-scoped `require_auth` is the security boundary.
 *
 * The organization list is NOT exhaustive: the registry has no on-chain
 * index and GeoLink has no mirror table yet, so it shows organizations
 * this browser knows about. The UI says so rather than implying
 * otherwise. See organizationService.ts for the details.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getOrgConfig,
  getOrganization,
  getOrgRoles,
  listOrganizations,
  rememberOrganization,
  forgetOrganization,
  buildRegisterOrganization,
  buildSetOrgStatus,
  buildSetOrgVerification,
  buildSetOrgVerificationV2,
  ORG_DIMENSION_GRANTS,
  buildGrantOrgRole,
  buildRevokeOrgRole,
  signAndSubmitOrgTx,
  mirrorOrganization,
  isOrgId,
  shortOrgId,
  ORG_STATUS_TRANSITIONS,
  type Organization,
  type OrgStatusName,
  type OrgRoleName,
  type OrgRoleReport,
  type OrgVerificationDimension,
  type OrgSigner,
  type BuiltOrgTx,
} from '../../v2/services/organizationService';
import { OrganizationSpatialBindings } from '../spatial-bindings/OrganizationSpatialBindings';
import { OrganizationAuthorityPanel } from '../authority/AuthorityPanel';
import { useOrganizationAuthorization } from '../../hooks/useAuthorization';

// ── Props ────────────────────────────────────────────────────────────────────

export interface OrganizationsSectionProps {
  /** Numeric ISO 3166-1 country code, or null for an unscoped overview. */
  countryCode: number | null;
  countryName?: string;
  /** The signing wallet. Writes are hidden when absent. */
  signer: OrgSigner | null;
  /** Address that will authorize writes — must be the country/protocol admin. */
  walletAddress: string | null;
  networkPassphrase?: string;
  /** True in the AdminPanel overview: list and inspect, never mutate. */
  readOnly?: boolean;
  /** Whether the connected wallet may administer organizations here. */
  canAdminister?: boolean;
}

// ── Styles ───────────────────────────────────────────────────────────────────

const card: React.CSSProperties = {
  padding: '16px',
  border: '1px solid #ddd',
  borderRadius: '8px',
  marginBottom: '12px',
  backgroundColor: '#f9f9f9',
};

const heading: React.CSSProperties = {
  margin: '0 0 12px 0', fontSize: '16px', fontWeight: 'bold',
};

const hint: React.CSSProperties = { fontSize: '12px', color: '#666' };

const input: React.CSSProperties = {
  padding: '6px', fontSize: '12px', border: '1px solid #ddd',
  borderRadius: '4px', boxSizing: 'border-box',
};

const button = (enabled: boolean, tone: 'primary' | 'danger' | 'plain' = 'primary'): React.CSSProperties => ({
  padding: '6px 14px',
  backgroundColor: !enabled ? '#ccc' : tone === 'danger' ? '#dc3545' : tone === 'plain' ? '#6c757d' : '#007bff',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: enabled ? 'pointer' : 'not-allowed',
  fontSize: '12px',
  fontWeight: 500,
});

const STATUS_TONE: Record<OrgStatusName, { bg: string; fg: string }> = {
  none:      { bg: '#e9ecef', fg: '#495057' },
  pending:   { bg: '#fff3cd', fg: '#856404' },
  active:    { bg: '#d4edda', fg: '#155724' },
  suspended: { bg: '#ffe5d0', fg: '#8a4b08' },
  revoked:   { bg: '#f8d7da', fg: '#721c24' },
};

const StatusPill: React.FC<{ status: OrgStatusName }> = ({ status }) => {
  const tone = STATUS_TONE[status] ?? STATUS_TONE.none;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
      backgroundColor: tone.bg, color: tone.fg, textTransform: 'capitalize',
    }}>
      {status}
    </span>
  );
};

const ROLE_LABELS: { id: OrgRoleName; label: string; blurb: string }[] = [
  { id: 'admin',       label: 'Admin',       blurb: 'manages the organization record' },
  { id: 'operator',    label: 'Operator',    blurb: 'acts on its spatial objects' },
  { id: 'beneficiary', label: 'Beneficiary', blurb: 'receives routed revenue' },
];

/**
 * How many organizations render before "show more".
 *
 * Collapsed rows are cheap — the expensive work is gated behind expansion,
 * which costs ten contract simulations. So this is about the operator, not
 * the network: a country with hundreds of organizations turns a single
 * scroll into a search problem, and the filter box next to this is the
 * real answer. The page size just keeps the initial paint short.
 */
const ORGS_PER_PAGE = 25;

// ── Component ────────────────────────────────────────────────────────────────

export const OrganizationsSection: React.FC<OrganizationsSectionProps> = ({
  countryCode,
  countryName,
  signer,
  walletAddress,
  networkPassphrase,
  readOnly = false,
  canAdminister = false,
}) => {
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [unresolved, setUnresolved] = useState<string[]>([]);
  /** True once the ids came from GeoLink's mirror rather than this browser. */
  const [enumerable, setEnumerable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [lookupId, setLookupId] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(ORGS_PER_PAGE);

  const canWrite = !readOnly && canAdminister && !!signer && !!walletAddress && countryCode !== null;

  // ── Load ───────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listOrganizations(
        countryCode !== null ? { country: countryCode } : {},
      );
      setOrgs(result.organizations);
      setUnresolved(result.unresolved);
      setEnumerable(result.complete);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, [countryCode]);

  useEffect(() => {
    let cancelled = false;
    getOrgConfig().then((cfg) => {
      if (cancelled) return;
      setConfigured(cfg.configured);
      if (cfg.configured) void load();
    });
    return () => { cancelled = true; };
  }, [load]);

  // ── Write plumbing ─────────────────────────────────────────────────────────

  /**
   * Every write is the same three steps; the only thing that varies is
   * which XDR gets built. Centralising it keeps the busy/error/reload
   * handling from drifting between six near-identical handlers.
   */
  const runWrite = useCallback(async (
    key: string,
    build: () => Promise<BuiltOrgTx>,
    successMessage: string,
    /** Organization whose mirror row this write invalidates, if any. */
    mirrorOrgId?: string,
  ) => {
    if (!signer) return;
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      const built = await build();
      const result = await signAndSubmitOrgTx(built, signer, networkPassphrase);
      let message = `${successMessage} (tx ${result.hash.slice(0, 8)}…)`;
      if (mirrorOrgId) {
        const outcome = await mirrorOrganization(mirrorOrgId, result.ledger);
        if (!outcome.mirrored && outcome.reason) message += ` — not mirrored: ${outcome.reason}`;
      }
      setNotice(message);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Transaction failed');
    } finally {
      setBusy(null);
    }
  }, [signer, networkPassphrase, load]);

  const handleRegister = useCallback(async () => {
    if (!canWrite || countryCode === null) return;
    const name = newName.trim();
    if (!name) { setError('Organization name is required'); return; }

    setBusy('register');
    setError(null);
    setNotice(null);
    try {
      const built = await buildRegisterOrganization({
        caller: walletAddress!, country: countryCode, name,
      });
      // Record the id before submitting: if signing succeeds but the
      // page reloads before we hear back, the organization would
      // otherwise exist on chain with nothing able to find it.
      rememberOrganization(built.orgId);
      const result = await signAndSubmitOrgTx(built, signer!, networkPassphrase);

      // Mirror immediately, before any object can be bound to this
      // organization: GeoLink's foreign key rejects an explicit
      // organization_id that it has never seen.
      const outcome = await mirrorOrganization(built.orgId, result.ledger);

      let message = `Registered "${name}" as ${shortOrgId(built.orgId)}… (tx ${result.hash.slice(0, 8)}…)`;
      if (!outcome.mirrored && outcome.reason) {
        message += ` — registered on chain, but not mirrored to GeoLink: ${outcome.reason}`;
      }
      setNotice(message);
      setNewName('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Registration failed');
    } finally {
      setBusy(null);
    }
  }, [canWrite, countryCode, newName, walletAddress, signer, networkPassphrase, load]);

  const handleLookup = useCallback(async () => {
    const id = lookupId.trim().toLowerCase();
    if (!isOrgId(id)) { setError('Organization id must be 64 hex characters'); return; }
    setBusy('lookup');
    setError(null);
    setNotice(null);
    try {
      const org = await getOrganization(id);
      if (!org || org.statusName === 'none') {
        setError(`No organization registered under ${shortOrgId(id)}…`);
        return;
      }
      if (countryCode !== null && org.numericCode !== countryCode) {
        setError(
          `${org.name || shortOrgId(id)} is registered in ` +
          `${org.alpha2 || org.numericCode}, not ${countryName || countryCode}.`,
        );
        return;
      }
      rememberOrganization(id);
      setLookupId('');
      setNotice(`Added ${org.name || shortOrgId(id)} to this browser's directory.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Lookup failed');
    } finally {
      setBusy(null);
    }
  }, [lookupId, countryCode, countryName, load]);

  // ── Render ─────────────────────────────────────────────────────────────────

  // Name and id both, because an operator arriving from a binding dialog or
  // a log line has the id and nothing else, and pasting a prefix should
  // find the row.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter(
      (o) => (o.name || '').toLowerCase().includes(q) || o.id.toLowerCase().includes(q),
    );
  }, [orgs, search]);

  const shown = filtered.slice(0, visibleCount);

  useEffect(() => { setVisibleCount(ORGS_PER_PAGE); }, [search]);

  const title = countryCode !== null
    ? `Organizations${countryName ? ` — ${countryName}` : ''}`
    : 'Organizations';

  if (configured === false) {
    return (
      <div style={card}>
        <h3 style={heading}>{title}</h3>
        <div style={hint}>
          The organization registry is not deployed on this network yet. Set{' '}
          <code>REACT_APP_V2_ORG_REGISTRY_ID</code> once{' '}
          <code>geo-org-registry</code> is deployed.
        </div>
      </div>
    );
  }

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
        <h3 style={{ ...heading, marginBottom: 0 }}>{title}</h3>
        <button onClick={() => void load()} disabled={loading} style={button(!loading, 'plain')}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <div style={{ ...hint, marginBottom: '12px' }}>
        Organizations own spatial objects and receive routed revenue.
        {countryCode !== null
          ? ' Registration and status changes are scoped to this country.'
          : ' Read-only overview.'}
      </div>

      {error && (
        <div style={{
          padding: '8px', marginBottom: '10px', borderRadius: '4px',
          backgroundColor: '#f8d7da', color: '#721c24', fontSize: '12px',
        }}>
          {error}
        </div>
      )}
      {notice && (
        <div style={{
          padding: '8px', marginBottom: '10px', borderRadius: '4px',
          backgroundColor: '#d4edda', color: '#155724', fontSize: '12px',
        }}>
          {notice}
        </div>
      )}

      {canWrite && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={`New organization name in ${countryName || countryCode}`}
            style={{ ...input, flex: 1 }}
          />
          <button
            onClick={() => void handleRegister()}
            disabled={busy !== null || !newName.trim()}
            style={button(busy === null && !!newName.trim())}
          >
            {busy === 'register' ? 'Signing…' : 'Register'}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        <input
          value={lookupId}
          onChange={(e) => setLookupId(e.target.value)}
          placeholder="Look up an organization by id (64 hex characters)"
          style={{ ...input, flex: 1, fontFamily: 'monospace' }}
        />
        <button
          onClick={() => void handleLookup()}
          disabled={busy !== null || !lookupId.trim()}
          style={button(busy === null && !!lookupId.trim(), 'plain')}
        >
          {busy === 'lookup' ? 'Looking up…' : 'Add'}
        </button>
      </div>

      {orgs.length > ORGS_PER_PAGE / 2 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by name or id"
            style={{ ...input, flex: 1 }}
          />
          <span style={hint}>
            {search.trim()
              ? `${filtered.length} of ${orgs.length}`
              : `${orgs.length} total`}
          </span>
        </div>
      )}

      {orgs.length === 0 && !loading && (
        <div style={{ ...hint, padding: '10px 0' }}>
          No organizations known here yet.
          {canWrite ? ' Register one above, or add an existing one by id.' : ''}
        </div>
      )}

      {orgs.length > 0 && filtered.length === 0 && (
        <div style={{ ...hint, padding: '10px 0' }}>
          No organization here matches “{search.trim()}”. An organization
          registered in another country will not appear in this list — look
          it up by id above.
        </div>
      )}

      {shown.map((org) => (
        <OrganizationRow
          key={org.id}
          org={org}
          expanded={expandedId === org.id}
          onToggle={() => setExpandedId(expandedId === org.id ? null : org.id)}
          canWrite={canWrite}
          busy={busy}
          signer={signer}
          walletAddress={walletAddress}
          networkPassphrase={networkPassphrase}
          onSetStatus={(status) => runWrite(
            `status:${org.id}`,
            () => buildSetOrgStatus(org.id, walletAddress!, status),
            `${org.name || shortOrgId(org.id)} is now ${status}`,
            org.id,
          )}
          onSetVerification={(verified) => runWrite(
            `verify:${org.id}`,
            () => buildSetOrgVerification(org.id, walletAddress!, verified),
            verified
              ? `${org.name || shortOrgId(org.id)} marked verified`
              : `Verification cleared for ${org.name || shortOrgId(org.id)}`,
            org.id,
          )}
          onSetVerificationV2={(dims) => runWrite(
            `verify:${org.id}`,
            () => buildSetOrgVerificationV2(org.id, walletAddress!, dims),
            `Verification updated for ${org.name || shortOrgId(org.id)}`,
            org.id,
          )}
          onRoleChange={(wallet, roles, grant) => runWrite(
            `role:${org.id}`,
            () => (grant ? buildGrantOrgRole : buildRevokeOrgRole)(
              org.id, walletAddress!, wallet, roles,
            ),
            `${grant ? 'Granted' : 'Revoked'} ${roles.join(', ')} for ${wallet.slice(0, 6)}…`,
          )}
          onForget={() => { forgetOrganization(org.id); void load(); }}
        />
      ))}

      {filtered.length > shown.length && (
        <button
          onClick={() => setVisibleCount((n) => n + ORGS_PER_PAGE)}
          style={{ ...button(true, 'plain'), width: '100%', marginTop: '4px' }}
        >
          Show {Math.min(ORGS_PER_PAGE, filtered.length - shown.length)} more
          {' '}({filtered.length - shown.length} remaining)
        </button>
      )}

      {unresolved.length > 0 && (
        <div style={{ ...hint, marginTop: '10px' }}>
          {unresolved.length} id{unresolved.length === 1 ? '' : 's'} did not
          resolve on chain:{' '}
          {unresolved.map(shortOrgId).join(', ')}. An id GeoLink mirrors but
          the registry does not know about was pushed without a matching
          registration.
        </div>
      )}

      <div style={{ ...hint, marginTop: '12px', fontStyle: 'italic' }}>
        {enumerable
          ? 'Listed from GeoLink\'s organization mirror; every field shown is '
            + 'read from the chain, which stays authoritative.'
          : 'This list covers organizations this browser has seen. The registry '
            + 'has no on-chain index and this GeoLink deployment has no mirror '
            + 'to enumerate, so it is not necessarily every organization'
            + (countryCode !== null ? ' in this country.' : '.')}
      </div>
    </div>
  );
};

// ── Row ──────────────────────────────────────────────────────────────────────

interface OrganizationRowProps {
  org: Organization;
  expanded: boolean;
  onToggle: () => void;
  canWrite: boolean;
  busy: string | null;
  /** Passed through to the spatial binding panel, which signs its own writes. */
  signer: OrgSigner | null;
  walletAddress: string | null;
  networkPassphrase?: string;
  onSetStatus: (status: OrgStatusName) => void;
  onSetVerification: (verified: boolean) => void;
  onSetVerificationV2: (dims: Record<OrgVerificationDimension, boolean>) => void;
  onRoleChange: (wallet: string, roles: OrgRoleName[], grant: boolean) => void;
  onForget: () => void;
}

const OrganizationRow: React.FC<OrganizationRowProps> = ({
  org, expanded, onToggle, canWrite, busy, signer, walletAddress, networkPassphrase,
  onSetStatus, onSetVerification, onSetVerificationV2, onRoleChange, onForget,
}) => {
  const [roleWallet, setRoleWallet] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<Set<OrgRoleName>>(new Set());

  const transitions = useMemo(
    () => ORG_STATUS_TRANSITIONS[org.statusName] ?? [],
    [org.statusName],
  );

  const rowBusy = busy !== null && busy.endsWith(org.id);
  /** Whose roles are being read and changed. Blank box means the operator's own. */
  const lookupWallet = (roleWallet.trim() || walletAddress || '').trim();
  const rolesReady = canWrite && lookupWallet.length > 0 && selectedRoles.size > 0 && !rowBusy;

  // Only while expanded: one of these costs ten contract simulations, and a
  // country's organization list can be long.
  const orgAuthorization = useOrganizationAuthorization(
    expanded ? org.id : null,
    walletAddress,
  );

  const toggleRole = (role: OrgRoleName) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(role)) next.delete(role); else next.add(role);
      return next;
    });
  };

  /*
    What the chain currently holds for the wallet in the box.

    The checkboxes above are a choice of what to grant next, not a record
    of what was granted — but they look exactly like a membership list,
    so an operator who ticks two boxes, reloads, and finds them empty
    reasonably concludes their grant vanished. It never existed: the
    Grant button is what signs. Showing the held roles turns a guess into
    a reading, and confirms a grant landed once it does.
  */
  const [heldRoles, setHeldRoles] = useState<OrgRoleReport | null>(null);
  const [rolesReading, setRolesReading] = useState(false);

  useEffect(() => {
    // A grant is only true once it is on chain, so this re-reads when a
    // write finishes — `rowBusy` falling is the signal one just did.
    if (!expanded || !lookupWallet || rowBusy) return undefined;

    let live = true;
    setRolesReading(true);
    const timer = setTimeout(() => {
      getOrgRoles(org.id, lookupWallet)
        .then((report) => { if (live) setHeldRoles(report); })
        .catch(() => { if (live) setHeldRoles(null); })
        .finally(() => { if (live) setRolesReading(false); });
    }, 350); // typing an address should not be 56 contract reads

    return () => { live = false; clearTimeout(timer); setRolesReading(false); };
  }, [expanded, org.id, lookupWallet, rowBusy]);

  const heldSummary = useMemo(() => {
    if (!heldRoles) return null;
    const held = (['admin', 'operator', 'beneficiary'] as OrgRoleName[])
      .filter((r) => heldRoles[r]);
    if (held.length === 0) return 'no roles';
    const dormant = held.filter((r) => !heldRoles.effective[r]);
    return dormant.length > 0
      ? `${held.join(', ')} — dormant while the organization is ${heldRoles.statusName}`
      : held.join(', ');
  }, [heldRoles]);

  return (
    <div style={{
      border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fff',
      marginBottom: '8px', overflow: 'hidden',
    }}>
      <div
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '10px 12px', cursor: 'pointer',
        }}
      >
        <span style={{ fontSize: '11px', color: '#888', width: '10px' }}>{expanded ? '▾' : '▸'}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {org.name || '(unnamed)'}
          </div>
          <div style={{ fontSize: '11px', color: '#888', fontFamily: 'monospace' }}>
            {shortOrgId(org.id)}… · {org.alpha2 || org.numericCode || '—'}
          </div>
        </div>
        {org.verification?.verified && (
          <span title="Verified" style={{ fontSize: '12px', color: '#155724' }}>✓ verified</span>
        )}
        <StatusPill status={org.statusName} />
      </div>

      {expanded && (
        <div style={{ padding: '0 12px 12px 12px', borderTop: '1px solid #eee' }}>
          <div style={{ ...hint, margin: '10px 0', wordBreak: 'break-all', fontFamily: 'monospace' }}>
            {org.id}
          </div>

          {org.createdBy && (
            <div style={{ ...hint, marginBottom: '10px' }}>
              Registered by {org.createdBy.slice(0, 6)}…{org.createdBy.slice(-4)}
              {org.createdAt ? ` at ledger time ${org.createdAt}` : ''}
            </div>
          )}

          {canWrite && transitions.length > 0 && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
              {transitions.map((next) => (
                <button
                  key={next}
                  onClick={() => onSetStatus(next)}
                  disabled={rowBusy}
                  style={button(!rowBusy, next === 'revoked' ? 'danger' : 'primary')}
                >
                  {next === 'revoked' ? 'Revoke' : `Set ${next}`}
                </button>
              ))}
            </div>
          )}

          {canWrite && (
            <VerificationPanel
              org={org}
              busy={rowBusy}
              onSetVerification={onSetVerification}
              onSetVerificationV2={onSetVerificationV2}
            />
          )}

          {canWrite && org.statusName === 'revoked' && (
            <div style={{ ...hint, marginBottom: '10px' }}>
              Revoked is terminal on chain — this organization cannot be
              reactivated, and no further roles can be granted.
            </div>
          )}

          {/*
            Rendered for read-only viewers as well. This is the view that
            answers "why can't I do X here", and someone who cannot act is
            exactly the person asking.
          */}
          <div style={{ marginBottom: '10px' }}>
            <OrganizationAuthorityPanel
              auth={orgAuthorization.auth}
              loading={orgAuthorization.loading}
              error={orgAuthorization.error}
              wallet={walletAddress}
              organizationName={org.name}
            />
          </div>

          {/*
            Rendered for read-only viewers too: which objects an organization
            holds is public on chain, and the AdminPanel overview is the one
            place a protocol admin can see it across countries. The panel
            gates its own buttons on `canWrite` and on the object-level
            authority it reads from the chain.
          */}
          <OrganizationSpatialBindings
            org={org}
            canWrite={canWrite && org.statusName !== 'revoked'}
            signer={signer}
            walletAddress={walletAddress}
            networkPassphrase={networkPassphrase}
          />

          {canWrite && org.statusName !== 'revoked' && (
            <div style={{ borderTop: '1px solid #eee', paddingTop: '10px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>Roles</div>
              <div style={{ ...hint, marginBottom: '8px' }}>
                {ROLE_LABELS.map((r) => `${r.label} ${r.blurb}`).join('; ')}.
                {org.statusName !== 'active' && ' Roles stay dormant until the organization is active.'}
              </div>
              <input
                value={roleWallet}
                onChange={(e) => setRoleWallet(e.target.value)}
                placeholder={walletAddress ? `Wallet address (blank = yours, ${walletAddress.slice(0, 6)}…)` : 'Wallet address'}
                style={{ ...input, width: '100%', marginBottom: '6px', fontFamily: 'monospace' }}
              />
              {lookupWallet && (
                <div style={{ ...hint, marginBottom: '8px' }}>
                  {rolesReading
                    ? 'Reading roles on chain…'
                    : `On chain now for ${lookupWallet.slice(0, 6)}…: ${heldSummary ?? 'could not be read'}`}
                </div>
              )}
              <div style={{ ...hint, marginBottom: '6px' }}>
                Tick what to grant, then press Grant and sign. The ticks are a
                choice, not a record — the line above is what the chain holds.
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
                {ROLE_LABELS.map((r) => (
                  <label key={r.id} style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <input
                      type="checkbox"
                      checked={selectedRoles.has(r.id)}
                      onChange={() => toggleRole(r.id)}
                    />
                    {r.label}
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => onRoleChange(lookupWallet, Array.from(selectedRoles), true)}
                  disabled={!rolesReady}
                  style={button(rolesReady)}
                >
                  Grant
                </button>
                <button
                  onClick={() => onRoleChange(lookupWallet, Array.from(selectedRoles), false)}
                  disabled={!rolesReady}
                  style={button(rolesReady, 'danger')}
                >
                  Revoke
                </button>
              </div>
            </div>
          )}

          <div style={{ borderTop: '1px solid #eee', paddingTop: '10px', marginTop: '10px' }}>
            <button onClick={onForget} style={button(true, 'plain')}>
              Remove from this browser&apos;s list
            </button>
            <span style={{ ...hint, marginLeft: '8px' }}>
              Local only — the organization stays on chain.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Verification ─────────────────────────────────────────────────────────────

const DIMENSIONS: { id: OrgVerificationDimension; label: string; capability: string }[] = [
  { id: 'identityVerified',    label: 'Identity',    capability: 'can own objects' },
  { id: 'authorityVerified',   label: 'Authority',   capability: 'can operate objects' },
  { id: 'beneficiaryVerified', label: 'Beneficiary', capability: 'can receive funds' },
];

interface VerificationPanelProps {
  org: Organization;
  busy: boolean;
  onSetVerification: (verified: boolean) => void;
  onSetVerificationV2: (dims: Record<OrgVerificationDimension, boolean>) => void;
}

/**
 * Three independent evidentiary bars, each gating exactly one capability.
 *
 * Falls back to the legacy single toggle when the deployed registry predates
 * the V2 upgrade, because the contract has no `set_verification_v2` to call
 * yet. In that state only identity is reachable, so the organization can be
 * recorded as an owner but can never act or be paid — which the panel says
 * out loud rather than leaving the operator to discover it.
 */
const VerificationPanel: React.FC<VerificationPanelProps> = ({
  org, busy, onSetVerification, onSetVerificationV2,
}) => {
  const v2 = org.verificationV2 ?? null;

  const onChain = useMemo<Record<OrgVerificationDimension, boolean>>(() => ({
    identityVerified:    v2 ? v2.identityVerified : !!org.verification?.verified,
    authorityVerified:   v2 ? v2.authorityVerified : false,
    beneficiaryVerified: v2 ? v2.beneficiaryVerified : false,
  }), [v2, org.verification?.verified]);

  const [draft, setDraft] = useState(onChain);

  // Re-seed whenever the chain state changes, so a completed write or a
  // reload does not leave the boxes showing a stale edit.
  useEffect(() => { setDraft(onChain); }, [onChain]);

  const dirty = DIMENSIONS.some(({ id }) => draft[id] !== onChain[id]);

  if (!v2) {
    return (
      <div style={{ borderTop: '1px solid #eee', paddingTop: '10px', marginBottom: '10px' }}>
        <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>Verification</div>
        <button
          onClick={() => onSetVerification(!org.verification?.verified)}
          disabled={busy}
          style={button(!busy, 'plain')}
        >
          {org.verification?.verified ? 'Clear verification' : 'Mark verified'}
        </button>
        <div style={{ ...hint, marginTop: '6px' }}>
          This registry predates the three-dimension verification upgrade, so
          only identity can be set. Until it is upgraded this organization can
          be recorded as an owner but can never operate objects or receive
          funds.
        </div>
      </div>
    );
  }

  return (
    <div style={{ borderTop: '1px solid #eee', paddingTop: '10px', marginBottom: '10px' }}>
      <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>Verification</div>
      <div style={{ ...hint, marginBottom: '8px' }}>
        Three independent checks, not a ladder. Each one unlocks exactly one
        capability, so an organization whose identity is known but whose wallet
        authority is unproven can own objects without being able to act or be paid.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
        {DIMENSIONS.map(({ id, label, capability }) => (
          <label
            key={id}
            title={ORG_DIMENSION_GRANTS[id]}
            style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <input
              type="checkbox"
              checked={draft[id]}
              disabled={busy}
              onChange={() => setDraft((p) => ({ ...p, [id]: !p[id] }))}
            />
            <span style={{ fontWeight: 600, minWidth: '78px' }}>{label}</span>
            <span style={{ color: '#888' }}>
              {capability}
              {draft[id] && !org.isActive && ' — dormant until active'}
            </span>
          </label>
        ))}
      </div>

      <button
        onClick={() => onSetVerificationV2(draft)}
        disabled={!dirty || busy}
        style={button(dirty && !busy)}
      >
        {busy ? 'Working…' : 'Apply verification'}
      </button>
      {dirty && (
        <span style={{ ...hint, marginLeft: '8px' }}>Unsaved changes.</span>
      )}
      {!org.isActive && (
        <div style={{ ...hint, marginTop: '6px' }}>
          Every capability also requires the organization to be active, so
          nothing takes effect while it is {org.statusName}.
        </div>
      )}
    </div>
  );
};

export default OrganizationsSection;
